import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatPrice, whatsappLink } from "@/lib/api";
import VerifiedBadge from "@/components/VerifiedBadge";
import InquiryForm from "@/components/InquiryForm";
import EMICalc from "@/components/EMICalc";
import { MapPin, Building2, CheckCircle2, Play, Home as HomeIcon, MessageCircle, Calendar, ArrowLeft } from "lucide-react";

export default function ProjectDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/projects/${id}`)
      .then((r) => setP(r.data))
      .catch((e) => setErr(e))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="max-w-7xl mx-auto px-6 py-24 text-center text-muted-foreground">Loading…</div>;
  if (err || !p) return (
    <div className="max-w-3xl mx-auto px-6 py-24 text-center">
      <div className="font-heading text-2xl text-forest mb-2">Project not found</div>
      <Link to="/projects" className="text-sm text-gold-hover underline">Back to all projects</Link>
    </div>
  );

  return (
    <div>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-6">
        <Link to="/projects" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-forest" data-testid="detail-back">
          <ArrowLeft className="w-4 h-4" /> All projects
        </Link>
      </div>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-4">
        <div className="grid lg:grid-cols-5 gap-5">
          <div className="lg:col-span-3 relative rounded-2xl overflow-hidden aspect-[16/10]">
            <img src={p.hero_image} alt={p.name} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            {p.verified && <div className="absolute top-4 left-4"><VerifiedBadge notes={p.verified_notes} /></div>}
            {p.video_url && (
              <a href={p.video_url} target="_blank" rel="noreferrer" className="absolute bottom-4 left-4 inline-flex items-center gap-2 bg-white text-forest px-3 py-2 rounded-full text-xs font-medium">
                <Play className="w-3.5 h-3.5 fill-forest" /> Watch 60-sec walkthrough
              </a>
            )}
          </div>
          <div className="lg:col-span-2 grid grid-cols-2 gap-5">
            {(p.gallery || []).slice(0, 4).map((g, i) => (
              <div key={i} className="rounded-xl overflow-hidden aspect-square bg-cream">
                <img src={g} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
            {(p.gallery || []).length === 0 && (
              <div className="col-span-2 rounded-xl border border-dashed border-border h-full flex items-center justify-center text-xs text-muted-foreground p-6 text-center">
                More photos coming soon.
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Info */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3">
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <MapPin className="w-4 h-4" /> {p.location}, {p.city}
            </div>
            <h1 className="font-heading font-extrabold text-3xl md:text-5xl text-forest tracking-tight leading-[1.05] mt-2" data-testid="detail-project-name">
              {p.name}
            </h1>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-2 flex-wrap">
              <Building2 className="w-4 h-4" /> by{" "}
              <Link
                to={`/builders/${p.builder_name.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
                data-testid="builder-name-link"
                className="text-forest font-medium underline decoration-gold/40 hover:decoration-gold hover:text-gold-hover underline-offset-2"
              >
                {p.builder_name}
              </Link>
              {p.rera_number ? (
                <span data-testid="rera-badge-registered" className="ml-3 text-xs px-2 py-0.5 rounded-full bg-forest/5 text-forest border border-forest/10">
                  RERA Registered · {p.rera_number}
                </span>
              ) : (
                <span data-testid="rera-badge-pending" className="ml-3 text-xs px-2 py-0.5 rounded-full bg-cream text-muted-foreground border border-border">
                  RERA: Not yet registered
                </span>
              )}
            </div>

            <div className="mt-6 flex items-end gap-4 flex-wrap">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Starting at</div>
                <div className="font-heading text-4xl md:text-5xl font-extrabold text-forest leading-none">
                  {formatPrice(p.price_from)}
                </div>
              </div>
              {p.price_to && p.price_to !== p.price_from && (
                <div className="text-sm text-muted-foreground mb-1">up to {formatPrice(p.price_to)}</div>
              )}
              {p.possession_date && (
                <div className="ml-auto text-right">
                  <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Possession</div>
                  <div className="font-heading font-bold text-forest">{p.possession_date}</div>
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {(p.bhk_options || []).map((b) => (
                <span key={b} className="text-xs px-3 py-1.5 rounded-full bg-cream border border-border text-forest">{b}</span>
              ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href={whatsappLink(`Hi, I'd like to book a site visit for ${p.name} at ${p.location}.`)}
                target="_blank" rel="noreferrer"
                data-testid="detail-whatsapp-cta"
                className="inline-flex items-center gap-2 rounded-full bg-whatsapp hover:brightness-95 text-white px-5 py-3 text-sm font-medium transition-all"
              >
                <MessageCircle className="w-4 h-4" /> WhatsApp Us
              </a>
              <a
                href="#inquiry"
                data-testid="detail-schedule-visit"
                className="inline-flex items-center gap-2 rounded-full bg-forest hover:bg-forest-hover text-white px-5 py-3 text-sm font-medium transition-all"
              >
                <Calendar className="w-4 h-4" /> Schedule Site Visit
              </a>
            </div>

            {p.verified && (
              <div className="mt-6 inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-gold/15 text-forest border border-gold/30" data-testid="bank-financed-badge">
                <CheckCircle2 className="w-3.5 h-3.5 text-forest" />
                Bank-Financed · site-visited
              </div>
            )}

            <p className="mt-8 text-muted-foreground leading-relaxed">{p.description}</p>

            {(p.highlights || []).length > 0 && (
              <div className="mt-8">
                <h3 className="font-heading font-bold text-forest text-lg mb-3">Highlights</h3>
                <ul className="grid sm:grid-cols-2 gap-2.5">
                  {p.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-gold flex-none mt-0.5" /> {h}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(p.amenities || []).length > 0 && (
              <div className="mt-10">
                <h3 className="font-heading font-bold text-forest text-lg mb-3">Amenities</h3>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {p.amenities.map((a) => (
                    <div key={a} className="flex items-center gap-2 text-sm text-forest">
                      <HomeIcon className="w-4 h-4 text-gold" /> {a}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(p.units || []).length > 0 && (
              <div className="mt-10">
                <h3 className="font-heading font-bold text-forest text-lg mb-3">Configurations & pricing</h3>
                <div className="rounded-xl border border-border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-cream">
                      <tr>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground">Configuration</th>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground">Size</th>
                        <th className="text-right px-4 py-3 font-medium text-muted-foreground">Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {p.units.map((u, i) => (
                        <tr key={i} className="border-t border-border/60">
                          <td className="px-4 py-3 font-medium text-forest">{u.label}</td>
                          <td className="px-4 py-3 text-muted-foreground">{u.size_sqft ? `${u.size_sqft} sqft` : "—"}</td>
                          <td className="px-4 py-3 text-right font-heading font-bold text-forest">{formatPrice(u.price)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="mt-14">
              <h3 className="font-heading font-bold text-forest text-lg mb-3">Estimate your EMI</h3>
              <EMICalc defaultLoan={Math.round(p.price_from * 0.8)} />
            </div>
          </div>

          <aside id="inquiry" className="lg:col-span-2">
            <div className="sticky top-24">
              <InquiryForm projectId={p.id} projectName={p.name} />
            </div>
          </aside>
        </div>
      </section>

      {/* Affordability lead capture */}
      <section className="bg-cream border-t border-border" data-testid="affordability-cta">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 text-center">
          <div className="text-[11px] uppercase tracking-[0.22em] text-gold font-bold">Find one in your range</div>
          <h2 className="mt-3 font-heading font-extrabold text-3xl md:text-4xl text-forest tracking-tight">
            What&apos;s your comfortable budget?
          </h2>
          <p className="mt-3 text-sm text-muted-foreground max-w-lg mx-auto">
            Priced so you still have a life after the EMI.
          </p>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto">
            {[
              { label: "Under ₹70L",  q: "max_price=7000000",                        testid: "budget-under-70L" },
              { label: "₹70L – 1Cr",  q: "min_price=7000000&max_price=10000000",     testid: "budget-70L-1Cr" },
              { label: "₹1 – 1.5Cr",  q: "min_price=10000000&max_price=15000000",    testid: "budget-1-1.5Cr" },
              { label: "₹1.5Cr+",     q: "min_price=15000000",                       testid: "budget-1.5Cr-plus" },
            ].map((b) => (
              <Link
                key={b.label}
                to={`/projects?${b.q}`}
                data-testid={b.testid}
                className="rounded-full bg-white border border-border hover:border-gold hover:bg-gold/5 text-forest font-heading font-bold text-sm h-12 flex items-center justify-center transition-all"
              >
                {b.label}
              </Link>
            ))}
          </div>
          <div className="mt-6">
            <a
              href={whatsappLink(`Hi, I'm looking for a home in Hyderabad but not sure of the budget yet. Can you help me find options?`)}
              target="_blank" rel="noreferrer"
              data-testid="affordability-whatsapp"
              className="inline-flex items-center gap-2 rounded-full bg-whatsapp hover:brightness-95 text-white px-6 h-11 text-sm font-medium transition-all"
            >
              <MessageCircle className="w-4 h-4" /> Chat with us on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
